import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import GenericTable from "../../../../Components/Tables/GenericTable";
import "../style.css";
import { getInitials } from "../../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#7C3AED",
  "#0EA5E9",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
];

const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ImageAvatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        className="fv-transactions__avatar"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: "#fff",
          background: getAvatarColor(name),
        }}
      >
        {getInitials(name)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="fv-transactions__avatar"
      style={{ objectFit: "cover" }}
      onError={() => setFailed(true)}
    />
  );
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const buildColumns = () => [
  {
    title: "Fleet Manager",
    dataIndex: "fleetManager",
    key: "fleetManager",
    enableSearch: true,
  },
  {
    title: "Date/Time",
    dataIndex: "date",
    key: "date",
    sorter: (a, b) =>
      (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    render: (text, record) => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          lineHeight: "1.2",
        }}
      >
        <span>{text}</span>
        <span style={{ color: "#71717A" }}>{record.time}</span>
      </div>
    ),
  },
  { title: "Txn Id", dataIndex: "txnId", key: "txnId", enableSearch: true },
  {
    title: "Driver",
    dataIndex: "driver",
    key: "driver",
    enableSearch: true,
    render: (name) => (
      <span className="fv-transactions__person-cell">
        <ImageAvatar src={null} name={name} />
        {name}
      </span>
    ),
  },
  {
    title: "Vehicle",
    dataIndex: "vehicle",
    key: "vehicle",
    enableSearch: true,
  },
  {
    title: "Mileage",
    dataIndex: "mileage",
    key: "mileage",
    sorter: (a, b) =>
      (parseFloat(String(a.mileage).replace(/[^0-9.]/g, "")) || 0) -
      (parseFloat(String(b.mileage).replace(/[^0-9.]/g, "")) || 0),
  },

  {
    title: "Fuel Level",
    dataIndex: "fuelLevel",
    key: "fuelLevel",
    sorter: (a, b) =>
      (parseFloat(a.fuelLevel) || 0) - (parseFloat(b.fuelLevel) || 0),
  },
  {
    title: "Vendor",
    dataIndex: "vendor",
    key: "vendor",
    enableSearch: "true",
    render: (text, record) => (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}
      >
        <ImageAvatar src={record.vendorAvatar} name={text} />
        {text}
      </span>
    ),
  },
  {
    title: "Station ID",
    dataIndex: "stationId",
    key: "stationId",
    enableSearch: true,
  },
  {
    title: "Address",
    dataIndex: "address",
    key: "address",
    enableSearch: true,
  },
  {
    title: "Fuel Type",
    dataIndex: "fuelType",
    key: "fuelType",
    onFilter: (value, record) => record.fuelType === value,
    render: (text) => <span className="fuel-type-badge">{text}</span>,
  },
  {
    title: "Fuel Added",
    dataIndex: "volume",
    key: "volume",
    sorter: (a, b) =>
      (parseFloat(String(a.volume).replace("L", "")) || 0) -
      (parseFloat(String(b.volume).replace("L", "")) || 0),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    sorter: (a, b) =>
      (Number(String(a.amount).replace(/[^0-9.-]/g, "")) || 0) -
      (Number(String(b.amount).replace(/[^0-9.-]/g, "")) || 0),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    onFilter: (value, record) => record.status === value,
    render: (status) => (
      <span className={`status-tag ${status?.toLowerCase()}`}>{status}</span>
    ),
  },
  {
    title: "Payment",
    dataIndex: "paymentStatus",
    key: "paymentStatus",
    onFilter: (value, record) => record.paymentStatus === value,
    render: (payment) =>
      payment === "-" ? (
        "-"
      ) : (
        <span
          className={`fv-transactions__payment-tag ${payment.toLowerCase()}`}
        >
          {payment}
        </span>
      ),
  },
];

const VendorTransactions = ({ setBtnConfigs, setExportData, transactions = [] }) => {
  const navigate = useNavigate();

  const handleRequest = () => {
    navigate("/request-adjustments");
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Request Adjustments",
        icon: <PlusOutlined />,
        onClick: handleRequest,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs]);

  const txnData = transactions.map((txn) => ({
    key: String(txn.id),
    fleetManager: txn.fleet_company_name || "-",
    date: txn.date || "-",
    time: txn.time || "-",
    txnId: txn.txn_id || "-",
    driver: txn.driver_name || "-",
    vehicle: txn.vehicle_id || "-",
    mileage:
      txn.mileage_at_transaction !== null &&
      txn.mileage_at_transaction !== undefined
        ? `${txn.mileage_at_transaction} km`
        : "-",
    fuelLevel: txn.fuel_level ? `${txn.fuel_level} L` : "-",
    vendor: txn.fuel_vendor_name || "-",
    vendorAvatar: resolveAvatarUrl(txn.fuel_vendor_avatar),
    stationId: txn.fuel_vendor_station_id || "-",
    address: txn.fuel_vendor_address || "-",
    fuelType: txn.fuel_type_name || "-",
    volume: txn.volume ? `${txn.volume}L` : "-",
    amount: formatCurrency(txn.amount),
    status: capitalize(txn.status) || "-",
    paymentStatus: capitalize(txn.payment_status) || "-",
    settlementId: txn.settlement_id || "-",
  }));

  const fuelTypeFilters = [
    ...new Set(txnData.map((item) => item.fuelType)),
  ].map((fuelType) => ({ text: fuelType, value: fuelType }));

  const statusFilters = [...new Set(txnData.map((item) => item.status))].map(
    (status) => ({ text: status, value: status }),
  );

  const paymentFilters = [
    ...new Set(
      txnData
        .map((item) => item.paymentStatus)
        .filter((payment) => payment !== "-"),
    ),
  ].map((payment) => ({ text: payment, value: payment }));

  const columns = buildColumns().map((column) => {
    if (column.key === "fuelType") return { ...column, filters: fuelTypeFilters };
    if (column.key === "status") return { ...column, filters: statusFilters };
    if (column.key === "paymentStatus")
      return { ...column, filters: paymentFilters };
    return column;
  });

  useEffect(() => {
    setExportData(txnData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions]);

  return (
    <div className="fv-transactions">
      <GenericTable
        dataSource={txnData}
        columns={columns}
        enableRowSelection={false}
        onRowClick={(r) =>
          navigate("/transaction-detail-vendor", { state: { id: r.key } })
        }
        onSelectionChange={(keys, rows) => console.log(keys, rows)}
      />
    </div>
  );
};

export default VendorTransactions;
